import ArrowIcon from "theme/components/icons/ArrowIcon";
import Link from "core-web/components/Link";
import PropTypes from "prop-types";
import React from "react";
import TextStyles from "theme/components/text/TextStyles";
import styled from "theme/libs/styled";

const shouldForwardProp = (prop) => ["noUnderline", "revertAnimation"].indexOf(prop) === -1;

const StyledLink = styled(Link, { shouldForwardProp })`
    position: relative;
    display: block;
    width: max-content;

    ${({ noUnderline }) =>
        !noUnderline &&
        `
            ::after {
                display: block;
                content: '';
                margin-top: 2px;
                border-bottom: solid 1px currentColor;
                transform: scaleX(0);
                transition: transform 250ms ease-in-out;
                transform-origin: 100% 50%;
                position: absolute;
                left: 0;
                right: 0;
                bottom: -3px;
            }

            &.active::after,
            :hover::after {
                transform: scaleX(1);
                transform-origin: 0 50%;
            }
        `}

    ${({ noUnderline, revertAnimation }) =>
        !noUnderline &&
        revertAnimation &&
        `
            ::after {
                transform: scaleX(1);
                transform-origin: 0 50%;
            }

            &.active::after,
            :hover::after {
                transform: scaleX(0);
                transform-origin: 100% 50%;
            }
        `}
`;

const StyledLinkWysisyg = styled(StyledLink)`
    color: var(--default-color-active);
`;

/**
 * TextLink
 *
 * @param {string} color - Set text color
 * @param {bool} noUnderline - If true, no underline will animate on hover
 * @param {string} size - Predefined sizes for the link.
 * @param {string} to - Link to internal or external url. Acts like a href.
 * @param {bool} withArrow - If true, the link will have a right arrow
 */

const TextLink = ({
    children = <></>,
    color = undefined,
    isActive = false,
    noUnderline = false,
    textStyles = undefined,
    size = "sm",
    to = "",
    gap = "8px",
    withArrow = false,
    revertAnimation = false,
    ...rest
}) => {
    const availableTypo = {
        xs: "Myriad Pro/12-100",
        sm: "Myriad Pro/14-100",
        md: "Myriad Pro/16-100",
        lg: "Myriad Pro/18-100",
        xl: "Myriad Pro/24-100",
    };

    const typography = availableTypo[size];

    return (
        <StyledLink
            to={to ? to : undefined}
            as={!to && "button"}
            noUnderline={noUnderline}
            revertAnimation={revertAnimation}
            color={color}
            className={isActive ? "active" : null}
            {...rest}
        >
            <TextStyles as="span" textStyles={textStyles || typography} display="flex" alignItems="center" gap={gap}>
                {children}
                {withArrow && <ArrowIcon height="0.65em" width="auto" color="currentColor" aria-hidden />}
            </TextStyles>
        </StyledLink>
    );
};

// Used to replace the default link when using WYSIWYG
export const TextLinkWysisyg = ({ attrs, children, ...rest }) => (
    <StyledLinkWysisyg tabIndex={attrs["tabIndex"]} to={attrs["data-url"]} {...rest}>
        {`(${children})`}
    </StyledLinkWysisyg>
);

TextLinkWysisyg.propTypes = {
    attrs: PropTypes.object,
    children: PropTypes.node.isRequired,
};

TextLinkWysisyg.defaultProps = {
    attrs: {},
};

// Used to replace the default link when using WYSIWYG and forces all links to taget="_black"
export const TextLinkWysisygForceTargetBlank = ({ attrs, children, ...rest }) => (
    <StyledLinkWysisyg
        rel="noopener noreferrer"
        tabIndex={attrs["tabIndex"]}
        target="_black"
        to={attrs["data-url"]}
        {...rest}
    >
        {`(${children})`}
    </StyledLinkWysisyg>
);

TextLinkWysisygForceTargetBlank.propTypes = {
    attrs: PropTypes.object,
    children: PropTypes.node.isRequired,
};

TextLinkWysisygForceTargetBlank.defaultProps = {
    attrs: {},
};

export default TextLink;
